"use client"

import { useEffect, type ReactNode } from "react"
import { MultiChoice, SingleChoice, YNU } from "./choice"
import {
  DATA_TYPES, INCIDENT_TYPES, MAGNITUDES, ORG_TYPES, SYSTEMS, US_STATES,
  type IncidentTypeId, type Intake, type Magnitude, type YesNoUnknown,
  BACKUPS_HINT, BACKUPS_TITLE, DEMANDS, GEOGRAPHY, HELP_OPTIONS, PAID_OPTIONS, asksDemand, demandMade, geographyOf, helpOf, withGeography, withHelp, type Demand, type GeographyId, questionCopy, asksEncryption, asksSystems, shown, withAnswer, type AnswerKey,
} from "@/lib/breach/intake"
import type { Workspace } from "@/lib/breach/workspace"
import type { WorkspaceUpdater } from "./incident-tools"

// datetime-local <-> ISO in local time (same pattern as the intake wizard and the clocks column)
const toIso = (local: string) => (local ? new Date(local).toISOString() : "")
function isoToLocal(iso: string | undefined): string {
  if (!iso) return ""
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ""
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset())
  return d.toISOString().slice(0, 16)
}

function Field({ id, title, hint, children }: {
  id: string; title: string; hint?: string; children: ReactNode
}) {
  return (
    <div id={id} tabIndex={-1}
      className="grid gap-2 scroll-mt-6 rounded-lg border border-border bg-card p-4 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-solid">
      <h3 className="font-semibold leading-snug">{title}</h3>
      {hint && <p className="text-sm text-muted-foreground">{hint}</p>}
      {children}
    </div>
  )
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="grid gap-3">
      <h2 className="text-lg font-semibold">{title}</h2>
      {children}
    </section>
  )
}

const YNU_KEYS = [
  "stillActive", "unauthorizedAccess", "dataTaken", "affectedInUS", "affectedOutsideUS",
  "encrypted", "backupsIntact", "hasInsurance", "hasCounsel", "hasIrProvider",
] as const
type YnuKey = (typeof YNU_KEYS)[number]

/**
 * "My answers": every intake question editable at any time, in one scrollable page. Every
 * change saves immediately and re-derives the plan and the deadline clocks. Fields have
 * stable ids (#field-dataTaken …) so open-question reminders can deep-link here.
 */
export function AnswersPage({ w, update }: { w: Workspace; update: WorkspaceUpdater }) {
  const i = w.intake
  const copy = questionCopy(i.incidentType)

  useEffect(() => {
    const id = window.location.hash.slice(1)
    if (!id) return
    const el = document.getElementById(id)
    if (el) {
      el.scrollIntoView({ block: "start" })
      el.focus()
    }
  }, [])

  const setIntake = <K extends keyof Intake>(k: K, v: Intake[K]) =>
    update((x) => ({ ...x, updatedAt: new Date().toISOString(), intake: { ...x.intake, [k]: v } }))
  const setYnu = (k: YnuKey, v: string) =>
    update((x) => ({ ...x, updatedAt: new Date().toISOString(), intake: withAnswer(x.intake, k as AnswerKey, v as YesNoUnknown) }))

  return (
    <div className="grid gap-6">
      <div>
        <h1 className="text-2xl font-bold sm:text-3xl">My answers</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Changing an answer updates your plan and your deadline clocks. Say &ldquo;I don&apos;t know&rdquo; whenever you
          don&apos;t, and come back when you do.
        </p>
      </div>

      <Section title="The basics">
        <Field id="field-label" title="Label" hint="Optional: a name just for you, to tell incidents apart. Kept in this browser only.">
          <input type="text" className="rounded-md border border-border bg-background p-3 text-base" placeholder="e.g., Warehouse ransomware"
            value={w.label} aria-label="Incident label"
            onChange={(e) => update((x) => ({ ...x, updatedAt: new Date().toISOString(), label: e.target.value }))} />
        </Field>

        <Field id="field-incidentType" title="What happened?">
          <SingleChoice name="incidentType" options={INCIDENT_TYPES} value={i.incidentType}
            onChange={(v) => setIntake("incidentType", v as IncidentTypeId)} />
        </Field>

        <Field id="field-stillActive" title={copy.stillActive.title}
          hint={copy.stillActive.hint}>
          <SingleChoice name="stillActive" options={YNU} value={shown(i, "stillActive")} onChange={(v) => setYnu("stillActive", v)} />
        </Field>

        <Field id="field-unauthorizedAccess" title={copy.unauthorizedAccess.prompt}
          hint="Some legal notification clocks don't start until this is answered.">
          <SingleChoice name="unauthorizedAccess" options={YNU} value={shown(i, "unauthorizedAccess")}
            onChange={(v) => setYnu("unauthorizedAccess", v)} />
        </Field>

        <Field id="field-dataTaken" title={copy.dataTaken.prompt}
          hint="Some legal notification clocks don't start until this is answered. Ask your lawyer what counts as knowing.">
          <SingleChoice name="dataTaken" options={YNU} value={shown(i, "dataTaken")} onChange={(v) => setYnu("dataTaken", v)} />
        </Field>
        {asksDemand(i.incidentType) && (
          <Field id="field-demand" title="Has anyone demanded money, or threatened to leak or publish data?" hint="A demand changes the advice: don't reply or pay before talking to your lawyer and law enforcement.">
            <SingleChoice name="demand" options={DEMANDS} value={shown(i, "demand")}
              onChange={(v) => update((x) => ({ ...x, updatedAt: new Date().toISOString(), intake: withAnswer(x.intake, "demand", v as Demand) }))} />
          </Field>
        )}
        {asksDemand(i.incidentType) && demandMade(i) && (
          <Field id="field-paid" title="Has any of it already been paid?" hint="Payments can carry sanctions risk. Your lawyer and law enforcement need to know.">
            <SingleChoice name="paid" options={PAID_OPTIONS} value={shown(i, "paid")} onChange={(v) => update((x) => ({ ...x, updatedAt: new Date().toISOString(), intake: withAnswer(x.intake, "paid", v as YesNoUnknown) }))} />
          </Field>
        )}
      </Section>

      <Section title="Timing">
        <Field id="field-noticedAt" title="When did you first notice it?"
          hint="An estimate is fine. Some deadlines are measured from discovery.">
          <label className="grid gap-1 text-sm">
            First noticed
            <input type="datetime-local" className="w-full max-w-xs rounded-md border border-border bg-background p-3 text-base"
              value={isoToLocal(i.noticedAt)} onChange={(e) => setIntake("noticedAt", toIso(e.target.value))} />
          </label>
        </Field>
        <Field id="field-confirmedAt" title="When did you confirm it was a real incident?" hint="Leave blank if not confirmed yet.">
          <label className="grid gap-1 text-sm">
            Confirmed it was an incident
            <input type="datetime-local" className="w-full max-w-xs rounded-md border border-border bg-background p-3 text-base"
              value={isoToLocal(i.confirmedAt)} onChange={(e) => setIntake("confirmedAt", toIso(e.target.value))} />
          </label>
        </Field>
      </Section>

      <Section title="Your organization">
        <Field id="field-orgTypes" title="What kind of organization are you?" hint="Pick all that apply. This decides which rules we point you to.">
          <MultiChoice options={ORG_TYPES} values={i.orgTypes} exclusive={["none"]}
            onChange={(v) => setIntake("orgTypes", v as Intake["orgTypes"])} />
        </Field>
      </Section>

      <Section title="Affected people">
        <Field id="field-affectedInUS" title="Where do the affected people live?" hint="Some privacy laws depend on where the people live.">
          <SingleChoice name="where" options={GEOGRAPHY} value={geographyOf(i)}
            onChange={(v) => update((x) => ({ ...x, updatedAt: new Date().toISOString(), intake: withGeography(x.intake, v as GeographyId) }))} />
        </Field>
        {i.affectedInUS !== "no" && (
          <Field id="field-affectedStates" title="Which states or territories do they live in?"
            hint="Select any you know. This tool doesn't cover state breach-notification laws yet, but your answers are saved for your hand-off summary.">
            <div className="grid max-h-72 grid-cols-2 gap-2 overflow-y-auto rounded-lg border border-border p-3 sm:grid-cols-3">
              {US_STATES.map((s) => (
                <label key={s.code} className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={i.affectedStates.includes(s.code)}
                    onChange={() => setIntake("affectedStates",
                      i.affectedStates.includes(s.code)
                        ? i.affectedStates.filter((x) => x !== s.code)
                        : [...i.affectedStates, s.code])} />
                  {s.name}
                </label>
              ))}
            </div>
          </Field>
        )}
      </Section>

      <Section title="Data involved">
        <Field id="field-dataTypes" title="What kinds of data might be involved?">
          <MultiChoice options={DATA_TYPES} values={i.dataTypes} exclusive={["none-known"]}
            onChange={(v) => setIntake("dataTypes", v as Intake["dataTypes"])} />
        </Field>
        {asksEncryption(i.incidentType) && (
          <Field id="field-encrypted" title={copy.encrypted.prompt} hint={copy.encrypted.why}>
            <SingleChoice name="encrypted" options={YNU} value={shown(i, "encrypted")} onChange={(v) => setYnu("encrypted", v)} />
          </Field>
        )}
        <Field id="field-magnitude" title="About how many people's data?" hint="Some rules add steps at 500 or more people.">
          <SingleChoice name="magnitude" options={MAGNITUDES} value={shown(i, "magnitude")} onChange={(v) => update((x) => ({ ...x, updatedAt: new Date().toISOString(), intake: withAnswer(x.intake, "magnitude", v as Magnitude) }))} />
        </Field>
      </Section>

      {asksSystems(i.incidentType) && (
        <Section title="Systems">
          <Field id="field-systems" title="What systems are affected?">
            <MultiChoice options={SYSTEMS} values={i.systems} exclusive={["unknown"]}
              onChange={(v) => setIntake("systems", v as Intake["systems"])} />
          </Field>
          <Field id="field-backupsIntact" title={BACKUPS_TITLE} hint={BACKUPS_HINT}>
            <SingleChoice name="backupsIntact" options={YNU} value={shown(i, "backupsIntact")} onChange={(v) => setYnu("backupsIntact", v)} />
          </Field>
        </Section>
      )}

      <Section title="Help you have">
        <Field id="field-hasInsurance" title="Which of these can you call today?" hint="This only changes the first items in your plan. We never contact them.">
          <MultiChoice options={HELP_OPTIONS} values={helpOf(i) ?? []} exclusive={["none"]}
            onChange={(v) => update((x) => ({ ...x, updatedAt: new Date().toISOString(), intake: withHelp(x.intake, v) }))} />
        </Field>
      </Section>

      <p className="text-xs text-muted-foreground">
        We don&apos;t ask for names, emails, company names, or IP addresses. Everything here stays in this browser.
      </p>
    </div>
  )
}
